import React from 'react';
import { useState } from 'react';
import CommonLayout from "../../layouts/CommonLayout";
import { uploadImage } from '../../../api/ImageApi';
import { changeProfileImage, changeName, deleteProfileImage, me } from '../../../api/MemberApi';
import { checkNameDuplicated } from '../../../api/AuthApi';
import useAuthStore from '../../../store/authStore';
import { IoPersonOutline } from 'react-icons/io5';
import { AlertModal } from '../../layouts/AlertModal'; // AlertModal import

const ProfileEditPage = () => {
    const { member, setMember } = useAuthStore();
    const [name, setName] = useState(member?.name || '');
    const [uploadedImageUrl, setUploadedImageUrl] = useState(member?.imageUrl || null);
    const [isLoading, setIsLoading] = useState(false);

    // 모달 상태 관리 (title 제거)
    const [alertState, setAlertState] = useState({ isVisible: false, message: '', callback: null });

    const getErrorMessage = (error) => {
        if (error.response && error.response.data && error.response.data.message) {
            if (error.response.data.code === 'E409-7') {
                const daysLeft = error.response.data.data?.daysLeft || 0;
                return `아직 닉네임을 변경할 수 없습니다. ${daysLeft}일 후에 변경 가능합니다.`;
            }
            return error.response.data.message;
        }
        return '오류가 발생했습니다. 잠시 후 다시 시도해주세요.';
    };

    const handleImageUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        try {
            setIsLoading(true);
            const response = await uploadImage(file);
            await changeProfileImage(response.id);
            setUploadedImageUrl(response.url);
            const updatedMember = await me();
            setMember(updatedMember);
            setAlertState({ isVisible: true, message: '프로필 사진이 변경되었습니다.' });
        } catch (error) {
            setAlertState({ isVisible: true, message: '파일 업로드에 실패했습니다. 파일은 10MB 이하로 업로드해주세요.' });
        } finally {
            setIsLoading(false);
        }
    };

    const handleImageDelete = async () => {
        try {
            setIsLoading(true);
            await deleteProfileImage();
            setUploadedImageUrl(null);
            const updatedMember = await me();
            setMember(updatedMember);
            setAlertState({ isVisible: true, message: '프로필 사진이 삭제되었습니다.' });
        } catch (error) {
            setAlertState({ isVisible: true, message: getErrorMessage(error) });
        } finally {
            setIsLoading(false);
        }
    };

    const handleNameChange = async () => {
        if (!name.trim()) {
            setAlertState({ isVisible: true, message: '닉네임을 입력해주세요.' });
            return;
        }

        try {
            setIsLoading(true);
            const duplicateResponse = await checkNameDuplicated(name);
            if (duplicateResponse.duplicated) {
                setAlertState({ isVisible: true, message: '이미 사용 중인 닉네임입니다.' });
                return;
            }

            await changeName(name);
            const updatedMember = await me();
            setMember(updatedMember);
            setAlertState({ isVisible: true, message: '닉네임이 변경되었습니다.' });
        } catch (error) {
            setAlertState({ isVisible: true, message: getErrorMessage(error) });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <CommonLayout title="프로필 수정">
            <AlertModal
                isVisible={alertState.isVisible}
                message={alertState.message}
                callback={alertState.callback}
                onClose={() => setAlertState({ isVisible: false, message: '', callback: null })}
            />
            <div className="p-4 max-w-lg mx-auto">
                {/* 프로필 사진 섹션 */}
                <div className="text-sm font-semibold">프로필 사진 변경</div>
                <div className="h-4"></div>
                <div className="flex flex-col items-center mb-8">
                    <div className="w-32 h-32 rounded-full bg-gray-200 overflow-hidden mb-4">
                        {uploadedImageUrl ? (
                            <img
                                src={uploadedImageUrl}
                                alt="프로필"
                                className="w-full h-full object-cover"
                            />
                        ) : (
                            <div className="w-full h-full flex items-center justify-center text-gray-400">
                                <IoPersonOutline size={40} />
                            </div>
                        )}
                    </div>

                    <div className="flex space-x-3">
                        <label className="px-4 py-2 bg-blue-600 text-white rounded-lg cursor-pointer">
                            업로드
                            <input
                                type="file"
                                accept="image/*"
                                onChange={handleImageUpload}
                                className="hidden"
                                disabled={isLoading}
                            />
                        </label>
                        <button
                            onClick={handleImageDelete}
                            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg"
                            disabled={!uploadedImageUrl || isLoading}
                        >
                            삭제
                        </button>
                    </div>
                </div>

                {/* 닉네임 섹션 */}
                <div className="text-sm font-semibold">닉네임 변경</div>
                <div className="h-4"></div>
                <div className="flex space-x-2">
                    <input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="닉네임을 입력하세요"
                        disabled={isLoading}
                    />
                    <button
                        onClick={handleNameChange}
                        className="px-4 py-2 bg-amber-500 text-white rounded-lg disabled:bg-gray-300"
                        disabled={isLoading || !name.trim() || name === member?.name}
                    >
                        닉네임 변경
                    </button>
                </div>
                <div className="text-xs text-amber-600 mt-2">
                    닉네임을 변경하면 30일 동안 다시 변경할 수 없습니다. <br />
                    닉네임은 다른 사용자와 중복되지 않아야 합니다.
                </div>

                {/* 밥 점수 설명 섹션 */}
                <div className="mt-8">
                    <div className="text-sm font-semibold mb-4">밥 점수 안내</div>

                    <div className="grid grid-cols-2 gap-2">
                        <div className="flex items-center space-x-2 p-2 bg-gray-50 rounded-lg">
                            <img src="/images/bapfriend_point01.png" alt="0~19점" className="w-8 h-8" />
                            <div>
                                <span className="text-xs text-gray-800 font-semibold block">쉰밥</span>
                                <span className="text-xs text-gray-500">0~19점</span>
                            </div>
                        </div>
                        <div className="flex items-center space-x-2 p-2 bg-gray-50 rounded-lg">
                            <img src="/images/bapfriend_point02.png" alt="20~39점" className="w-8 h-8" />
                            <div>
                                <span className="text-xs text-gray-800 font-semibold block">찬밥</span>
                                <span className="text-xs text-gray-500">20~39점</span>
                            </div>
                        </div>
                        <div className="flex items-center space-x-2 p-2 bg-gray-50 rounded-lg">
                            <img src="/images/bapfriend_point03.png" alt="40~59점" className="w-8 h-8" />
                            <div>
                                <span className="text-xs text-gray-800 font-semibold block">그냥 밥</span>
                                <span className="text-xs text-gray-500">40~59점</span>
                            </div>
                        </div>
                        <div className="flex items-center space-x-2 p-2 bg-gray-50 rounded-lg">
                            <img src="/images/bapfriend_point04.png" alt="60~79점" className="w-8 h-8" />
                            <div>
                                <span className="text-xs text-gray-800 font-semibold block">따뜻한 밥</span>
                                <span className="text-xs text-gray-500">60~79점</span>
                            </div>
                        </div>
                        <div className="flex items-center space-x-2 p-2 bg-gray-50 rounded-lg">
                            <img src="/images/bapfriend_point05.png" alt="80~99점" className="w-8 h-8" />
                            <div>
                                <span className="text-xs text-gray-800 font-semibold block">뜨거운 밥</span>
                                <span className="text-xs text-gray-500">80~99점</span>
                            </div>
                        </div>
                        <div className="flex items-center space-x-2 p-2 bg-gray-50 rounded-lg">
                            <img src="/images/bapfriend_point06.png" alt="100점" className="w-8 h-8" />
                            <div>
                                <span className="text-xs text-gray-800 font-semibold block">황금누룽지</span>
                                <span className="text-xs text-gray-500">100점</span>
                            </div>
                        </div>
                    </div>
                    <div className="text-xs text-amber-600 mt-2">
                        모임 참여 시 밥친구들의 매너평가에 따라 밥 점수가 변동될 수 있습니다.
                    </div>
                </div>
            </div>
        </CommonLayout>
    );
};

export default ProfileEditPage;
